import { useMemo } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { Image } from 'expo-image';
import { WEB_HOVER_TRANSITION } from '../../../shared/constants/animations';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../shared/helpers/imageWebAlt.helper';
import { IDLE_COIN_SOURCE, getCdnUrlExactKey, ASSET_PATHS } from '../../../../constants/assets';
import { useTheme } from '../../../theme/useTheme';
import { shopSegmentedControlStyles } from './ShopSegmentedControl.styles';

export type ShopTab = 'coins' | 'pro';

export interface ShopSegmentedControlProps {
  value: ShopTab;
  onChange: (tab: ShopTab) => void;
}

export function ShopSegmentedControl({ value, onChange }: ShopSegmentedControlProps) {
  const theme = useTheme();
  const styles = useMemo(() => shopSegmentedControlStyles(theme), [theme]);
  const crownUri = getCdnUrlExactKey(ASSET_PATHS.CROWN_ICON);

  return (
    <View style={styles.container}>
      {(['coins', 'pro'] as const).map((tab) => {
        const isActive = value === tab;
        return (
          <Pressable
            key={tab}
            accessibilityRole="button"
            accessibilityState={{ selected: isActive }}
            onPress={() => onChange(tab)}
            style={({ pressed, hovered }) => [
              styles.segment,
              isActive ? styles.segmentActive : styles.segmentInactive,
              Platform.select({ web: { transition: WEB_HOVER_TRANSITION } as object }),
              pressed && styles.segmentPressed,
              hovered && Platform.OS === 'web' && !isActive && styles.segmentHoverWeb,
            ]}
          >
            <View style={styles.segmentInner}>
              {tab === 'coins' ? (
                <Image source={IDLE_COIN_SOURCE} style={styles.tabIcon} alt={WEB_IMAGE_ALT_DECORATIVE} contentFit="contain" />
              ) : (
                <Image
                  source={{ uri: crownUri }}
                  style={styles.tabIconCrown}
                  alt={WEB_IMAGE_ALT_DECORATIVE}
                  contentFit="contain"
                  tintColor={isActive ? '#1A1208' : theme.accent}
                />
              )}
              <Text style={[styles.label, isActive ? styles.labelActive : styles.labelInactive]}>
                {tab === 'coins' ? 'Coins' : 'Pro'}
              </Text>
            </View>
          </Pressable>
        );
      })}
    </View>
  );
}
